import type { ComponentType, ReactNode } from "react"
import { memo, useCallback, useMemo, useRef, useState } from "react"
import { Link, useNavigate } from "@tanstack/react-router"
import { IconCircleCheckFill18 } from "nucleo-ui-fill-18"
import {
	IconChartTrendUpOutline18,
	IconChevronDownOutline18,
	IconChevronRightOutline18,
	IconCompassOutline18,
	IconFileContentOutline18,
	IconGearOutline18,
} from "nucleo-ui-outline-18"
import { createPortal } from "react-dom"

import { cn } from "@productlane/lib/src/browser"
import { Button, LinearIcon, Tooltip } from "@productlane/ui"
import { portalGroupCardClass } from "@productlane/ui/src/styles/portal-group-card"
import { subtleSurfaceRing } from "@productlane/ui/src/styles/surface"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type {
	BatchDeclineResult,
	BatchDeclineTarget,
	BatchRow,
	BatchSection,
} from "./agent-batch"
import type { AgentItem } from "./agent-items"
import type { AgentSetupId } from "./agent-setup"
import { SelectionCheckbox } from "@/components/selection-checkbox"
import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { stepIndex } from "@/lib/hooks/use-list-row-selection"
import { useRole } from "@/lib/useRole"
import { mutateOk, useAuthData, useZero, useZeroWorkspace } from "@/lib/zero"
import { resolveDefaultTeamId } from "../threads/hooks/resolve-default-team-id"
import { useSyncedTeams } from "../threads/hooks/useSelectedTeams"
import {
	batchAcceptInput,
	batchDeclineTargets,
	batchGroups,
	batchIssueTargets,
	batchStats,
	hiddenRowCount,
	isIssueRow,
	isToggleable,
	needsYouSections,
	runBatchDecline,
	shownRows,
} from "./agent-batch"
import { agentPath } from "./agent-route"
import { useAgentSetupDismissals } from "./agent-setup-dismissals"
import { ArticleMention } from "./article-mention"
import { DeclineButton, isTyping } from "./decline-button"
import { useProposalGroupRows } from "./docs-change"
import { DocsAddedText } from "./docs-change-text"
import { ideaPlanParts } from "./format"
import { FADE_OUT_LABEL } from "./idea-list"
import { SeverityIcon } from "./recommendation-severity"
import { RecommendationTypeIcon } from "./recommendation-type-icon"
import {
	DISMISS_REASONS,
	DOCS_DECLINE_REASONS,
	rejectDocsGroup,
	reopenProposals,
} from "./use-recommendation-actions"

// Rows scrolling under a still pointer after a key press must not steal the cursor.
const MOUSE_IGNORE_MS = 300

// The portal's white group panel, minus overflow-hidden so the header can stick.
const CARD = cn(
	"rounded-lg bg-white dark:bg-gray-800",
	subtleSurfaceRing,
	"shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_1px_2px_rgba(15,17,21,0.04),0_6px_14px_-12px_rgba(15,17,21,0.18)]",
	"dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_1px_2px_rgba(0,0,0,0.5)]",
)

const keepInView = (node: HTMLLIElement | null) =>
	node?.scrollIntoView?.({ block: "nearest" })

const Row = memo(
	({
		row,
		skipped,
		onToggle,
		checkboxSlot,
		isCursor,
		followCursor,
		onHover,
	}: {
		row: BatchRow
		skipped: boolean
		onToggle?: () => void
		// Keeps rows without a checkbox in line with the ones that have one.
		checkboxSlot: boolean
		isCursor: boolean
		// The keyboard moved here, so the row scrolls into view.
		followCursor: boolean
		onHover: (id: string) => void
	}) => (
		<li
			ref={isCursor && followCursor ? keepInView : undefined}
			data-testid="agent-batch-row"
			data-cursor={isCursor ? "true" : undefined}
			data-skipped={skipped ? "true" : undefined}
			onMouseMove={() => onHover(row.item.id)}
			className={cn(
				"group/row relative flex h-10 items-center gap-3 border-t border-gray-200 px-4 dark:border-gray-700/80",
				isCursor && "bg-gray-100 dark:bg-gray-750",
			)}
		>
			{onToggle ? (
				<span className="relative z-[1] flex h-5 flex-none items-center">
					<SelectionCheckbox
						checked={!skipped}
						onCheckedChange={onToggle}
						label={skipped ? "Include this change" : "Leave this change out"}
					/>
				</span>
			) : (
				checkboxSlot && <span className="w-3.5 flex-none" />
			)}
			{/* Stretched over the whole row, so the gaps and edges open it too. */}
			<Link
				to={agentPath("all", row.item.id)}
				className={cn(
					"flex min-w-0 flex-1 items-center gap-3 after:absolute after:inset-0 after:content-['']",
					skipped && "opacity-50",
				)}
			>
				<RecommendationTypeIcon
					className="transform-gpu"
					type={row.item.type}
					articleIcon={
						"articleIcon" in row.item ? row.item.articleIcon : undefined
					}
				/>
				<span className={cn(FADE_OUT_LABEL, "text-sm text-primary")}>
					{row.title}
				</span>
				<span className="hidden flex-none text-xs text-tertiary sm:block">
					{row.source}
				</span>
			</Link>
			{/* Rows without an estimate keep the slot, so every source label lines up. */}
			{row.item.kind === "recommendation" ? (
				<SeverityIcon
					className="relative z-[1]"
					impactPerMonth={row.item.recommendation.impactPerMonth}
				/>
			) : (
				<span aria-hidden className="w-3.5 flex-none" />
			)}
		</li>
	),
)

interface RowControls {
	skipped: ReadonlySet<string>
	onToggle?: (id: string) => void
	cursorId: string | null
	followCursor: boolean
	onHover: (id: string) => void
}

const Group = memo(
	({
		section,
		expanded,
		onExpand,
		checkboxSlot,
		controls,
	}: {
		section: BatchSection
		expanded: boolean
		onExpand: (id: string) => void
		checkboxSlot: boolean
		controls: RowControls
	}) => {
		const hidden = hiddenRowCount(section.rows)
		const Chevron = expanded
			? IconChevronDownOutline18
			: IconChevronRightOutline18
		return (
			<section data-testid={`agent-batch-group-${section.id}`}>
				<ul className="list-none">
					{shownRows(section.rows, expanded).map((row) => (
						<Row
							key={row.item.id}
							row={row}
							skipped={controls.skipped.has(row.item.id)}
							onToggle={
								controls.onToggle && isToggleable(row)
									? () => controls.onToggle?.(row.item.id)
									: undefined
							}
							checkboxSlot={checkboxSlot}
							isCursor={controls.cursorId === row.item.id}
							followCursor={controls.followCursor}
							onHover={controls.onHover}
						/>
					))}
				</ul>
				{hidden > 0 && (
					<button
						type="button"
						data-testid="agent-batch-more"
						aria-expanded={expanded}
						onClick={() => onExpand(section.id)}
						className="flex h-10 w-full items-center gap-3 border-t border-gray-200 px-4 text-sm text-secondary hover:bg-gray-50 hover:text-primary dark:border-gray-700/80 dark:hover:bg-gray-750"
					>
						<Chevron className="size-4 flex-none transform-gpu" />
						{expanded ? "Show less" : `Show ${hidden} more`}
					</button>
				)}
			</section>
		)
	},
)

// Stays in view while the list scrolls, so the action is one click away.
const CardHeader = ({
	stats,
	action,
}: {
	stats: ReactNode
	action: ReactNode
}) => (
	<div className="sticky top-0 z-10 flex items-center justify-between gap-4 rounded-t-lg bg-white px-4 py-3.5 dark:bg-gray-800">
		<div className="flex flex-wrap items-center gap-1.5">{stats}</div>
		{action}
	</div>
)

const Stat = ({
	icon: Icon,
	iconClassName,
	value,
	label,
	tooltip,
}: {
	icon: ComponentType<{ className?: string }>
	iconClassName?: string
	value: string
	label: string
	tooltip: string
}) => (
	<Tooltip label={tooltip} side="bottom" contentClassName="max-w-64">
		<span className="flex h-6 cursor-default items-center gap-1.5 rounded-md px-2 text-xs text-secondary ring-1 ring-inset ring-gray-200 hover:bg-gray-50 hover:ring-gray-300 dark:ring-gray-700 dark:hover:bg-gray-700/50 dark:hover:ring-gray-600">
			<Icon
				className={cn(
					"size-3.5 flex-none transform-gpu text-tertiary",
					iconClassName,
				)}
			/>
			<span className="font-semibold tabular-nums text-primary">{value}</span>
			{label}
		</span>
	</Tooltip>
)

// Space on a row: what the agent would change, and where, without leaving the
// list, like Linear's peek. The reason comes after.
export const PeekCard = memo(({ row }: { row: BatchRow }) => {
	const { item } = row
	const rec = item.kind === "recommendation" ? item.recommendation : null
	const groupId =
		item.kind === "docs" ? item.group.id : (rec?.proposalGroupId ?? null)
	const docs = useProposalGroupRows(groupId)
	const planType =
		rec?.type ?? (item.kind === "docs" ? ("CONTENT" as const) : null)
	const plan = planType ? ideaPlanParts(planType, docs.rows) : null
	const firstRow = docs.rows[0]
	const articleIcon =
		firstRow?.proposals.find((p) => p.proposedIcon)?.proposedIcon ??
		(firstRow?.articleId
			? docs.articles.get(firstRow.articleId)?.icon
			: undefined) ??
		"file"
	return (
		<div
			data-testid="agent-batch-peek"
			className="pointer-events-auto flex max-h-[70dvh] flex-col gap-4 overflow-y-auto rounded-xl bg-white p-5 shadow-lg ring-1 ring-black/5 dark:bg-gray-800 dark:ring-white/10"
		>
			<div className="flex flex-col gap-1.5">
				<span className="flex items-center gap-2 text-xs text-tertiary">
					<RecommendationTypeIcon
						type={item.type}
						articleIcon={"articleIcon" in item ? item.articleIcon : undefined}
						className="h-3.5 w-3.5"
					/>
					{row.source}
				</span>
				<h3 className="text-base font-medium leading-6 text-primary">
					{row.title}
				</h3>
				{rec && rec.impactPerMonth > 0 && (
					<span className="text-xs text-tertiary">
						+{rec.impactPerMonth} chats a month
					</span>
				)}
			</div>
			{plan && (
				<p className="text-sm leading-6 text-primary">
					{plan.lead}
					{plan.article && (
						<>
							{" "}
							<ArticleMention
								title={plan.article.title}
								articleId={plan.article.articleId}
								icon={articleIcon}
							/>
						</>
					)}
					{plan.tail}
				</p>
			)}
			{docs.rows.length > 0 ? (
				<div className="text-sm leading-6">
					<DocsAddedText rows={docs.rows} articles={docs.articles} />
				</div>
			) : (
				<p className="border-l-2 border-gray-200 pl-4 text-sm leading-6 text-primary dark:border-gray-700">
					{row.detail}
				</p>
			)}
			{/* The reason, after the change; skipped when the line above already says it. */}
			{rec && (docs.rows.length > 0 || rec.summary !== row.detail) && (
				<p className="text-[13px] leading-5 text-secondary">{rec.summary}</p>
			)}
		</div>
	)
})

// The same writes as Decline on a single card, so the docs agent still learns
// from the reason and note.
function useBatchDecline(onDeclined: () => void) {
	const zero = useZero()
	const authData = useAuthData()
	const dismissSetup = useAgentSetupDismissals((state) => state.dismiss)
	const restoreSetup = useAgentSetupDismissals((state) => state.restore)
	const [busy, setBusy] = useState(false)
	const report = (error: unknown, action: string, id?: string) =>
		captureError(error, {
			domain: "general",
			action,
			workspaceId: authData.workspaceId,
			userId: authData.userId,
			extra: id ? { id } : undefined,
		})

	const undo = ({ undo: opened }: BatchDeclineResult) => {
		const onError = (error: unknown) =>
			report(error, "undo agent batch decline")
		reopenProposals(zero, opened.proposalIds, onError)
		for (const id of opened.recommendationIds) {
			void zero
				.mutate(mutators.agentRecommendations.reopen({ id }))
				.client.catch(onError)
		}
		for (const id of opened.setupIds) {
			restoreSetup(authData.workspaceId, id as AgentSetupId)
		}
	}

	const decline = async (
		targets: readonly BatchDeclineTarget[],
		reason: string,
		note: string,
	) => {
		if (busy || targets.length === 0) return
		setBusy(true)
		try {
			const result = await runBatchDecline(targets, {
				rejectDocs: async (target) => {
					// Read before the reject closes them, so Undo knows what to reopen.
					const group = target.proposalGroupId
						? await zero.run(queries.proposalGroupById(target.proposalGroupId))
						: undefined
					const ids = group
						? group.proposals
								.filter((p) => p.status === "OPEN" || p.status === "DRAFT")
								.map((p) => p.id)
						: target.proposalIds
					await rejectDocsGroup({
						zero,
						proposalGroupId: target.proposalGroupId,
						proposalIds: target.proposalIds,
						reason,
						note,
					})
					return ids
				},
				dismiss: async (id) => {
					await mutateOk(
						zero.mutate(mutators.agentRecommendations.dismiss({ id, reason })),
					)
				},
				dismissSetup: (id) =>
					dismissSetup(authData.workspaceId, id as AgentSetupId),
				onError: (error, target) =>
					report(error, "decline agent change in batch", target.id),
			})
			onDeclined()
			const { declined, failed } = result
			const action = { label: "Undo", callback: () => undo(result) }
			if (failed.length === 0) {
				toast.success({
					title:
						declined.length === 1
							? "Declined 1 change"
							: `Declined ${declined.length} changes`,
					action,
				})
			} else {
				toast.error({
					title: `Couldn't decline ${failed.length} of ${targets.length} changes`,
					...(declined.length > 0 && { action }),
				})
			}
		} finally {
			setBusy(false)
		}
	}
	return { decline, busy }
}

// Docs and guidance each get their own card, so each Accept applies one kind.
const ReadyCard = memo(
	({
		section,
		canAct,
		expanded,
		onExpand,
		controls,
		onDecided,
	}: {
		section: BatchSection
		canAct: boolean
		expanded: boolean
		onExpand: (id: string) => void
		controls: RowControls
		onDecided: () => void
	}) => {
		const stats = batchStats(section.rows, controls.skipped)
		const declineBatch = useBatchDecline(onDecided)
		const accept = api.ai.acceptAgentChanges.useMutation({
			onSuccess: ({ accepted, failed }) => {
				onDecided()
				if (failed.length === 0) {
					toast.success({ title: `Accepted ${accepted.length} changes` })
				} else {
					toast.info({
						title: `Accepted ${accepted.length}, ${failed.length} need a look`,
						description: failed[0]?.message,
					})
				}
			},
			onError: () => toast.error({ title: "Couldn't accept the changes" }),
		})
		return (
			<div className={CARD}>
				<CardHeader
					stats={
						<>
							{stats.impact > 0 && (
								<Stat
									icon={IconChartTrendUpOutline18}
									value={`+${stats.impact}`}
									label="chats a month"
									tooltip="Conversations a month the agent should resolve once these changes are live."
								/>
							)}
							{stats.checked > 0 && (
								<Stat
									icon={IconCircleCheckFill18}
									iconClassName={
										stats.verified === stats.checked
											? "text-green-500"
											: undefined
									}
									value={`${stats.verified}/${stats.checked}`}
									label="passed the re-run"
									tooltip={`The agent answered the real questions again with each change. ${stats.verified} of ${stats.checked} answered every question.`}
								/>
							)}
						</>
					}
					action={
						canAct && (
							<div className="flex flex-none items-center gap-1.5">
								<DeclineButton
									label={
										stats.selected === 1
											? "Decline 1 change"
											: `Decline all ${stats.selected}`
									}
									reasons={
										section.id === "docs"
											? DOCS_DECLINE_REASONS
											: DISMISS_REASONS
									}
									disabled={
										stats.selected === 0 ||
										declineBatch.busy ||
										accept.isLoading
									}
									onDecline={(reason, note) =>
										void declineBatch.decline(
											batchDeclineTargets(section.rows, controls.skipped),
											reason,
											note,
										)
									}
								/>
								<Button
									data-testid={`agent-batch-accept-${section.id}`}
									isLoading={accept.isLoading}
									disabled={stats.selected === 0 || declineBatch.busy}
									onClick={() =>
										accept.mutate(
											batchAcceptInput(section.rows, controls.skipped),
										)
									}
								>
									{stats.selected === 1
										? "Accept 1 change"
										: `Accept all ${stats.selected}`}
								</Button>
							</div>
						)
					}
				/>
				<Group
					section={section}
					expanded={expanded}
					onExpand={onExpand}
					checkboxSlot={false}
					controls={controls}
				/>
			</div>
		)
	},
)

const LINEAR_SETTINGS_PATH = "/settings/integrations/linear"

// Connectors need an engineer, so the one-click action here files their Linear
// issues in the workspace's default team; setup steps open from their row.
const NeedsYouCard = memo(
	({
		rows,
		canAct,
		expanded,
		onExpand,
		controls,
		onCreated,
	}: {
		rows: readonly BatchRow[]
		canAct: boolean
		expanded: ReadonlySet<string>
		onExpand: (id: string) => void
		controls: RowControls
		onCreated: () => void
	}) => {
		const navigate = useNavigate()
		const workspace = useZeroWorkspace()
		const teams = useSyncedTeams()
		const teamId = resolveDefaultTeamId(workspace?.defaultLinearTeamId, teams)
		const targets = batchIssueTargets(rows, controls.skipped)
		const count = targets.recommendationIds.length + targets.setupSteps.length
		const declineTargets = batchDeclineTargets(rows, controls.skipped)
		const declineCount = declineTargets.length
		const dismissSetup = useAgentSetupDismissals((state) => state.dismiss)
		const declineBatch = useBatchDecline(onCreated)
		const create =
			api.agentSetupActions.createIssuesFromRecommendations.useMutation({
				onSuccess: ({ created, failed }) => {
					onCreated()
					// A filed setup step is tracked in Linear now, so it leaves the list.
					const setupIds = new Set(targets.setupSteps.map((step) => step.id))
					for (const { id } of created) {
						if (workspace && setupIds.has(id)) {
							dismissSetup(workspace.id, id as AgentSetupId)
						}
					}
					if (failed.length === 0) {
						toast.success({
							title: `Created ${created.length} Linear issues`,
							icon: LinearIcon,
						})
					} else {
						toast.info({
							title: `Created ${created.length}, ${failed.length} need a look`,
							description: failed[0]?.message,
							icon: LinearIcon,
						})
					}
				},
				onError: () => toast.error({ title: "Couldn't create the issues" }),
			})
		const onCreate = () => {
			if (!teamId) {
				toast.info({
					title: "Pick a Linear team for new issues first",
					icon: LinearIcon,
					action: {
						label: "Open Linear settings",
						callback: () => void navigate({ to: LINEAR_SETTINGS_PATH }),
					},
				})
				return
			}
			create.mutate({
				recommendationIds: targets.recommendationIds,
				setupIssues: targets.setupSteps.map((step) => ({
					id: step.id,
					title: step.title,
					description: `${step.body}\n\nSet it up in Productlane: ${window.location.origin}${step.path}`,
				})),
				teamId,
			})
		}
		const issueRows = rows.filter(isIssueRow)
		const toggleable = rows.some(isToggleable)
		const impact = rows.reduce(
			(sum, row) =>
				targets.recommendationIds.includes(row.item.id) &&
				row.item.kind === "recommendation"
					? sum + row.item.recommendation.impactPerMonth
					: sum,
			0,
		)
		return (
			<div data-testid="agent-batch-needs-you" className={CARD}>
				<CardHeader
					stats={
						<>
							{impact > 0 && (
								<Stat
									icon={IconChartTrendUpOutline18}
									value={`+${impact}`}
									label="chats a month"
									tooltip="Conversations a month the agent could resolve once these are built."
								/>
							)}
						</>
					}
					action={
						canAct &&
						toggleable && (
							<div className="flex flex-none items-center gap-1.5">
								<DeclineButton
									label={
										declineCount === 1
											? "Decline 1"
											: `Decline all ${declineCount}`
									}
									reasons={DISMISS_REASONS}
									disabled={
										declineCount === 0 || declineBatch.busy || create.isLoading
									}
									onDecline={(reason, note) =>
										void declineBatch.decline(declineTargets, reason, note)
									}
								/>
								{issueRows.length > 0 && (
									<Button
										data-testid="agent-batch-create-issues"
										Icon={LinearIcon}
										isLoading={create.isLoading}
										disabled={count === 0 || declineBatch.busy}
										onClick={onCreate}
									>
										{count === 1
											? "Create 1 Linear issue"
											: `Create ${count} Linear issues`}
									</Button>
								)}
							</div>
						)
					}
				/>
				{needsYouSections(rows).map((section) => (
					<Group
						key={section.id}
						section={section}
						expanded={expanded.has(section.id)}
						onExpand={onExpand}
						checkboxSlot={canAct && toggleable}
						controls={controls}
					/>
				))}
			</div>
		)
	},
)

// Each group in the request portal's gray box: its icon and headline, one line
// on what it holds, then the white panel of rows.
const GROUP_COPY: Record<
	string,
	{
		title: string
		description: string
		icon: ComponentType<{ className?: string }>
	}
> = {
	docs: {
		title: "Content",
		description: "Help center articles the agent would add or fix.",
		icon: IconFileContentOutline18,
	},
	guidance: {
		title: "Guidance",
		description: "Rules for how the agent answers and when it hands off.",
		icon: IconCompassOutline18,
	},
	"needs-you": {
		title: "Setup",
		description: "What the agent needs from you and your engineers.",
		icon: IconGearOutline18,
	},
}

const GroupBox = memo(
	({
		groupId,
		testId,
		children,
	}: {
		groupId: string
		testId: string
		children: ReactNode
	}) => {
		const copy = GROUP_COPY[groupId]
		return (
			<div data-testid={testId} className={portalGroupCardClass}>
				{copy && (
					<div className="flex items-center gap-3 px-2.5 pb-2.5 pt-2">
						{/* On the gray box, so the tile is white, like the step markers. */}
						<span className="flex size-8 flex-none items-center justify-center rounded-md bg-white shadow-card dark:bg-gray-800 dark:shadow-card-dark">
							<copy.icon className="size-[18px] flex-none text-secondary" />
						</span>
						<div className="flex min-w-0 flex-col gap-0.5">
							<h2 className="text-sm font-medium text-primary">{copy.title}</h2>
							<p className="truncate text-xs text-tertiary">
								{copy.description}
							</p>
						</div>
					</div>
				)}
				{children}
			</div>
		)
	},
)

// Every open change in two cards: what Accept all applies, and what needs a
// person first. Like the inbox, J/K or the arrows move a cursor, X ticks the
// row, Space peeks at it and Enter opens it.
export const BatchReview = memo(
	({
		items,
		peekSlot,
	}: {
		items: readonly AgentItem[]
		// Where the peek floats, at the top right of the page.
		peekSlot: HTMLElement | null
	}) => {
		const { role } = useRole()
		const canAct = role !== "VIEWER"
		const navigate = useNavigate()
		const [skipped, setSkipped] = useState<ReadonlySet<string>>(new Set())
		const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
		const [cursorId, setCursorId] = useState<string | null>(null)
		const [peekOpen, setPeekOpen] = useState(false)
		const [followCursor, setFollowCursor] = useState(false)
		const lastKeyAt = useRef(0)
		const groups = useMemo(() => batchGroups(items), [items])
		const rows = useMemo(() => groups.flatMap((g) => g.rows), [groups])
		const ready = useMemo(
			() =>
				groups
					.filter((group) => group.id !== "needs-you")
					.map((group): BatchSection => ({ id: group.id, rows: group.rows })),
			[groups],
		)
		const needsYouRows = useMemo(
			() => groups.find((group) => group.id === "needs-you")?.rows ?? [],
			[groups],
		)
		const sections = useMemo(
			() => [...ready, ...needsYouSections(needsYouRows)],
			[ready, needsYouRows],
		)
		const visible = useMemo(
			() =>
				sections.flatMap((section) =>
					shownRows(section.rows, expanded.has(section.id)),
				),
			[sections, expanded],
		)
		const cursorIndex = visible.findIndex((row) => row.item.id === cursorId)
		const cursorRow = cursorIndex >= 0 ? visible[cursorIndex] : undefined
		const toggle = useCallback(
			(id: string) =>
				setSkipped((prev) => {
					const next = new Set(prev)
					if (next.has(id)) next.delete(id)
					else next.add(id)
					return next
				}),
			[],
		)
		const expand = useCallback(
			(id: string) =>
				setExpanded((prev) => {
					const next = new Set(prev)
					if (next.has(id)) next.delete(id)
					else next.add(id)
					return next
				}),
			[],
		)
		const hover = useCallback((id: string) => {
			if (Date.now() - lastKeyAt.current < MOUSE_IGNORE_MS) return
			setFollowCursor(false)
			setCursorId(id)
		}, [])
		const step = (direction: 1 | -1) => {
			const index = stepIndex(
				cursorIndex >= 0 ? cursorIndex : null,
				visible.length,
				direction,
			)
			if (index === null) return
			lastKeyAt.current = Date.now()
			setFollowCursor(true)
			setCursorId(visible[index]?.item.id ?? null)
		}

		const keys = { enabled: visible.length > 0, ignoreEventWhen: isTyping }
		useShortcut("down, j", () => step(1), { ...keys, preventDefault: true })
		useShortcut("up, k", () => step(-1), { ...keys, preventDefault: true })
		useShortcut(
			"space",
			() => {
				if (cursorRow) setPeekOpen((open) => !open)
			},
			{ ...keys, preventDefault: true },
		)
		useShortcut(
			"x",
			() => {
				if (canAct && cursorRow && isToggleable(cursorRow)) {
					toggle(cursorRow.item.id)
				}
			},
			keys,
		)
		useShortcut(
			"enter",
			() => {
				if (cursorRow)
					void navigate({ href: agentPath("all", cursorRow.item.id) })
			},
			{
				...keys,
				// A focused link or button already acts on Enter.
				ignoreEventWhen: (event) =>
					isTyping(event) ||
					(event.target instanceof HTMLElement &&
						!!event.target.closest("a, button")),
			},
		)
		useShortcut("esc", () => setPeekOpen(false), {
			enabled: peekOpen,
		})

		const controls: RowControls = {
			skipped,
			onToggle: canAct ? toggle : undefined,
			cursorId,
			followCursor,
			onHover: hover,
		}

		if (rows.length === 0) return null
		return (
			<div
				data-testid="agent-batch-review"
				className="flex min-w-0 flex-col gap-5"
				// A hover cursor follows the pointer out; the peek keeps its row.
				onMouseLeave={() => {
					if (!peekOpen && !followCursor) setCursorId(null)
				}}
			>
				{ready.map((section) => (
					<GroupBox
						key={section.id}
						groupId={section.id}
						testId={`agent-batch-ready-note-${section.id}`}
					>
						<ReadyCard
							section={section}
							canAct={canAct}
							expanded={expanded.has(section.id)}
							onExpand={expand}
							controls={controls}
							onDecided={() => setSkipped(new Set())}
						/>
					</GroupBox>
				))}
				{needsYouRows.length > 0 && (
					<GroupBox groupId="needs-you" testId="agent-batch-needs-you-note">
						<NeedsYouCard
							rows={needsYouRows}
							canAct={canAct}
							expanded={expanded}
							onExpand={expand}
							controls={controls}
							onCreated={() => setSkipped(new Set())}
						/>
					</GroupBox>
				)}
				{peekOpen &&
					cursorRow &&
					peekSlot &&
					createPortal(<PeekCard row={cursorRow} />, peekSlot)}
			</div>
		)
	},
)
